import { type CSSProperties } from "react";
import { KeycapButton } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

const Plus = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/**
 * Pin --primary to the brand navy (+ a light foreground) for the whole demo so
 * the primary key reads as the rich top-lit navy gradient in BOTH themes — the
 * lib's shadcn --primary inverts to near-white under .dark, which would wash the
 * key out. The unified adoption makes the same pin (handoff §1c). secondary /
 * destructive / outline / ghost are left on the theme tokens so they flip.
 */
const NAVY_PRIMARY = {
  "--primary": "212 72% 16%",
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

/**
 * Verification harness for KeycapButton — the "offset key" in the hex-notch
 * silhouette. Verify in a real browser (shape() clip + the travel block):
 * the dark travel path must TRACE the notch behind the key (a real sibling
 * block, not a sliced box-shadow), the keyline is a continuous nx-rim ring,
 * and the key presses onto its block with a springy bounce on `:active`.
 */
export function KeycapButtonDemo() {
  return (
    <Section
      id="keycap-button"
      title="KeycapButton"
      description="The Onyx 'offset key' in the brand's notched-card shape. A solid notched block sits down-right BEHIND the key (the travel path — a real sibling element, so it survives the notch clip where a box-shadow/drop-shadow would be sliced/faint); on :active the key translates flush onto it with a springy bounce. primary is raised most (3px navy gradient); secondary + destructive are simpler (2px); outline + ghost stay flat but still press down 1px so every button shares the depth + bounce. Every variant but ghost carries a continuous nx-rim keyline ring."
    >
      <VariantBlock
        label="The family — raise hierarchy (hover + press are live)"
        caption="primary raised most (3px) · secondary/destructive simpler (2px) · outline/ghost flat but press down 1px"
      >
        <div className="flex flex-wrap items-center gap-4" style={NAVY_PRIMARY}>
          <KeycapButton variant="primary">
            <Plus />
            Create
          </KeycapButton>
          <KeycapButton variant="secondary">Open</KeycapButton>
          <KeycapButton variant="destructive">Delete</KeycapButton>
          <KeycapButton variant="outline">Cancel</KeycapButton>
          <KeycapButton variant="ghost">Skip</KeycapButton>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Notch chamfer — t (subtlest) → l (largest)"
        caption="the travel block tracks the same silhouette; b is the button default"
      >
        <div className="flex flex-wrap items-end gap-4" style={NAVY_PRIMARY}>
          <KeycapButton notch="t">nx-t</KeycapButton>
          <KeycapButton notch="b">nx-b</KeycapButton>
          <KeycapButton notch="m">nx-m</KeycapButton>
          <KeycapButton notch="s">nx-s</KeycapButton>
          <KeycapButton notch="l">nx-l</KeycapButton>
        </div>
      </VariantBlock>

      <VariantBlock label="Size — sm · default · lg" caption="every variant scales with size">
        <div className="flex flex-wrap items-center gap-4" style={NAVY_PRIMARY}>
          <KeycapButton size="sm">Small</KeycapButton>
          <KeycapButton size="default">Default</KeycapButton>
          <KeycapButton size="lg">Large</KeycapButton>
          <KeycapButton size="default" variant="secondary">
            Open
          </KeycapButton>
        </div>
      </VariantBlock>

      <VariantBlock
        label="asChild — the key face is the consumer's element"
        caption="renders an <a> as the key (notch + ring + travel block + press all carry over)"
      >
        <div className="flex flex-wrap items-center gap-4" style={NAVY_PRIMARY}>
          <KeycapButton asChild>
            <a href="#keycap-button">Anchor key</a>
          </KeycapButton>
          <KeycapButton asChild variant="secondary">
            <a href="#keycap-button">Anchor secondary</a>
          </KeycapButton>
        </div>
      </VariantBlock>
    </Section>
  );
}
